import { useState } from "react";
import { MemoryRouter, Route, Switch, useHistory } from "react-router-dom";
import {
  ModalManager,
  useModal,
} from "@/react/components/Layout/ModalManagerProvider";
import type { MarketingEmailTemplateContentType } from "@/react/types/MarketingEmailTemplates";
import { FullScreenModal } from "@circle-react-shared/ModalsV2/FullScreenModal";
import { useIsVisualEmailBuilderEnabled } from "../VisualEmailBuilder/useIsVisualEmailBuilderEnabled";
import { ListTemplates } from "./ListTemplates";
import { ListTemplatesV2 } from "./ListTemplatesV2";
import { PreviewTemplate } from "./PreviewTemplate";
import type { SelectedCategory } from "./TemplateContents";
import type { ContentType } from "./TemplateContents/TemplateFilter";
import { useEmailMarketingTemplatePaths } from "./TemplatePathContext";
import { useNavigateContentWithApplyTemplate } from "./hooks/useNavigateContentWithApplyTemplate";

export const TEMPLATES_MODAL_ID = "broadcast-templates-modal" as const;

function TemplatesModalComponent() {
  const modal = useModal();
  const history = useHistory();
  const { contentPath } = useEmailMarketingTemplatePaths();
  const isVisualBuilderEnabled = useIsVisualEmailBuilderEnabled();
  const { handleNavigateContentWithApplyTemplate } =
    useNavigateContentWithApplyTemplate();
  const [selectedCategory, setSelectedCategory] =
    useState<SelectedCategory>("saved_templates");
  const [contentType, setContentType] = useState<ContentType>("all");

  const handleApply = async (
    templateId: number,
    contentType: MarketingEmailTemplateContentType,
  ) => {
    await handleNavigateContentWithApplyTemplate(templateId, contentType);
    void modal.hide();
  };

  const navigateToTiptapEditor = () => {
    void modal.hide();
    history.push(contentPath);
  };

  return (
    <FullScreenModal isOpen={modal.visible} size="full" onClose={modal.hide}>
      <MemoryRouter
        initialEntries={[`/templates/:templateId`, `/templates`]}
        initialIndex={1}
      >
        <Switch>
          <Route path="/templates/:templateId">
            <PreviewTemplate onApply={handleApply} />
          </Route>
          <Route path="/templates">
            {isVisualBuilderEnabled ? (
              <ListTemplatesV2
                onClose={modal.hide}
                onApply={handleApply}
                selectedCategory={selectedCategory}
                onSelectCategory={setSelectedCategory}
                contentType={contentType}
                onChangeContentType={setContentType}
              />
            ) : (
              <ListTemplates
                onStartFromScratch={navigateToTiptapEditor}
                onClose={modal.hide}
                onApply={handleApply}
              />
            )}
          </Route>
        </Switch>
      </MemoryRouter>
    </FullScreenModal>
  );
}

export const TemplatesModal = ModalManager.create(TemplatesModalComponent);
